import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import WorkOutlinedIcon from '@mui/icons-material/WorkOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    background: '#F8F9FF',
    padding: theme.spacing(20, 37.5),
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(20, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(12, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  headingSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(12),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(8),
    },
  },
  heading: {
    ...theme.typography['h3-bold'],
    color: '#2E3558',
    marginBottom: theme.spacing(3),
  },
  subHeading: {
    ...theme.typography['body01-regular'],
    color: theme.palette.neutral['clr-600'],
    maxWidth: '560px',
    margin: '0 auto',
  },
  contentSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(15),
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(10),
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(8),
    },
  },
  leftSection: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  boxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 116px)',
    gridTemplateRows: 'repeat(3, 116px)',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(3, 88px)',
      gridTemplateRows: 'repeat(3, 88px)',
      gap: theme.spacing(2),
    },
  },
  outerBox: {
    width: '116px',
    height: '116px',
    borderRadius: '12px',
    background: theme.palette.shades['clr-white-900'],
    border: '2px solid #E4E4E7',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 108, 73, 0.12)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '88px',
      height: '88px',
      borderRadius: '10px',
      gap: theme.spacing(1),
    },
  },
  activeBox: {
    border: '2px solid #006C49',
    boxShadow: '0 4px 16px rgba(0, 108, 73, 0.15)',
  },
  centerBox: {
    width: '116px',
    height: '116px',
    borderRadius: '12px',
    background: '#0F172A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '88px',
      height: '88px',
      borderRadius: '10px',
      padding: theme.spacing(1.5),
    },
  },
  boxIcon: {
    fontSize: '26px !important',
    color: '#006C49',
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px !important',
    },
  },
  boxLabel: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '10px',
    fontWeight: 700,
    color: '#2E3558',
    textAlign: 'center',
    letterSpacing: '0.6px',
    lineHeight: 1.3,
    whiteSpace: 'pre-line',
    [theme.breakpoints.down('sm')]: {
      fontSize: '8px',
    },
  },
  centerBoxLabel: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '10px',
    fontWeight: 700,
    color: theme.palette.shades['clr-white-900'],
    textAlign: 'center',
    letterSpacing: '0.6px',
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
    [theme.breakpoints.down('sm')]: {
      fontSize: '8px',
    },
  },
  rightSection: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  panelCard: {
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderRadius: '16px',
    padding: theme.spacing(6),
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E4E4E7',
    width: '440px',
    height: '360px',
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
      width: '360px',
      height: '340px',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      maxWidth: '480px',
      height: '340px',
    },
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  panelIconBox: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    backgroundColor: '#0F172A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  panelIcon: {
    color: theme.palette.shades['clr-white-900'],
    fontSize: '22px !important',
  },
  panelTitle: {
    ...theme.typography['h5-bold'],
    color: '#2E3558',
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h6-bold'],
    },
  },
  panelDescription: {
    ...theme.typography['body02-regular'],
    color: theme.palette.neutral['clr-600'],
    marginBottom: theme.spacing(5),
    lineHeight: 1.6,
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2.5),
  },
  itemCard: {
    backgroundColor: '#F4F5FA',
    borderRadius: '8px',
    padding: theme.spacing(3),
  },
  itemName: {
    ...theme.typography['body02-semiBold'],
    color: theme.palette.neutral['clr-900'],
    marginBottom: theme.spacing(0.5),
  },
  itemTag: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '10px',
    fontWeight: 700,
    color: theme.palette.neutral['clr-500'],
    letterSpacing: '0.6px',
  },
}));

const boxes = [
  {
    id: 0,
    label: 'ROLE',
    Icon: WorkOutlinedIcon,
    gridColumn: '2',
    gridRow: '1',
    panel: {
      title: 'Job Roles',
      description: 'Targeted case structures that mirror the actual interview format of your chosen track.',
      items: [
        { name: 'Management Consulting', tag: 'MBB STANDARD' },
        { name: 'Product Management', tag: 'FAANG STRATEGY' },
        { name: "Founder's Office", tag: 'VENTURE BACKED' },
        { name: 'Operations & Strategy', tag: 'GROWTH EQUITY' },
      ],
    },
  },
  {
    id: 1,
    label: 'SPECIALIZATION',
    Icon: TuneOutlinedIcon,
    gridColumn: '1',
    gridRow: '2',
    panel: {
      title: 'Specialization',
      description: 'Sharpen your edge in the function you\'re targeting with case prep tailored to the frameworks and metrics that matter.',
      items: [
        { name: 'Finance', tag: 'VALUATION & M&A' },
        { name: 'Marketing', tag: 'BRAND & GTM' },
        { name: 'Human Resources', tag: 'TALENT & ORG DESIGN' },
        { name: 'Entrepreneurship', tag: '0-TO-1 BUILDING' },
      ],
    },
  },
  {
    id: 2,
    label: 'COMPANY',
    Icon: BusinessOutlinedIcon,
    gridColumn: '3',
    gridRow: '2',
    panel: {
      title: 'Company',
      description: 'Train on the exact case formats each firm tests — from MBB-style profitability deep-dives to Amazon\'s working-backwards method.',
      items: [
        { name: 'BCG', tag: 'PROFITABILITY & MARKET CASES' },
        { name: 'Google', tag: 'PRODUCT SENSE CASES' },
        { name: 'Amazon', tag: 'WORKING-BACKWARDS CASES' },
        { name: 'Netflix', tag: 'GROWTH STRATEGY CASES' },
      ],
    },
  },
  {
    id: 3,
    label: 'INDUSTRY &\nDOMAIN',
    Icon: FilterListOutlinedIcon,
    gridColumn: '2',
    gridRow: '3',
    panel: {
      title: 'Industry & Domain',
      description: 'Master the case type that defines each industry so no prompt ever catches you off guard.',
      items: [
        { name: 'E-commerce', tag: 'HIGH-GROWTH SECTOR' },
        { name: 'FMCG', tag: 'CLASSIC CASE STUDIES' },
        { name: 'Healthcare', tag: 'REGULATED MARKETS' },
        { name: 'Aviation', tag: 'OPS-HEAVY CASES' },
      ],
    },
  },
];

const CaseDomainSection = () => {
  const classes = useStyles();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const timerRef = React.useRef(null);

  const startTimer = React.useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % boxes.length);
    }, 3000);
  }, []);

  React.useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const handleBoxClick = (id) => {
    setActiveIndex(id);
    startTimer();
  };

  const activeBox = boxes.find((b) => b.id === activeIndex);

  return (
    <Box className={classes.container}>
      <Box className={classes.headingSection}>
        <Typography className={classes.heading}>Master Your Specific Domain</Typography>
        <Typography className={classes.subHeading}>
          Practice cases tailored to your target role, company, specialization, and industry.
        </Typography>
      </Box>

      <Box className={classes.contentSection}>
        <Box className={classes.leftSection}>
          <Box className={classes.boxGrid}>
            {boxes.map((box) => (
              <Box
                key={box.id}
                className={`${classes.outerBox} ${activeIndex === box.id ? classes.activeBox : ''}`}
                sx={{ gridColumn: box.gridColumn, gridRow: box.gridRow }}
                onClick={() => handleBoxClick(box.id)}
              >
                <box.Icon className={classes.boxIcon} />
                <Typography className={classes.boxLabel}>{box.label}</Typography>
              </Box>
            ))}

            <Box className={classes.centerBox} sx={{ gridColumn: '2', gridRow: '2' }}>
              <Typography className={classes.centerBoxLabel}>
                {'CASE\nMASTER\nAI'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={classes.rightSection}>
          <Box className={classes.panelCard}>
            <Box className={classes.panelHeader}>
              <Box className={classes.panelIconBox}>
                <activeBox.Icon className={classes.panelIcon} />
              </Box>
              <Typography className={classes.panelTitle}>{activeBox.panel.title}</Typography>
            </Box>
            <Typography className={classes.panelDescription}>
              {activeBox.panel.description}
            </Typography>
            <Box className={classes.itemsGrid}>
              {activeBox.panel.items.map((item, idx) => (
                <Box key={idx} className={classes.itemCard}>
                  <Typography className={classes.itemName}>{item.name}</Typography>
                  <Typography className={classes.itemTag}>{item.tag}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseDomainSection;
